import UserDetailRow from '@features/employerAccount/components/UserDetailRow/UserDetailRow';
import { Stack } from '@mui/material';
import { white } from '@styles/colors';
import { getCookie } from '@utils/getCookie';
import { FC, useState } from 'react';

import ConfirmationModal from './ConfirmationModal';
import TemplateCompanyNameModal from './TemplateCompanyNameModal';
import TemplatePageCustomization from './TemplatePageCustomization';
import { EnterpriseTemplate } from '../types';

type UpdateData = {
  label: string;
  type: string;
  formDataKey: string;
  defaultValue: string | null | undefined;
  showEditor?: boolean;
  showTextArea?: boolean;
};
type TemplateJobPageCustomizationTabProps = {
  templateData: EnterpriseTemplate;
};

const TemplateJobPageCustomizationTab: FC<
  TemplateJobPageCustomizationTabProps
> = ({ templateData }) => {
  const dontShowWarningPopup = getCookie('dontShowWarningPopup');
  const isAppliedChanges = templateData.apply_changes && !dontShowWarningPopup;
  const [pendingUpdate, setPendingUpdate] = useState<UpdateData | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [updateData, setUpdateData] = useState<UpdateData>({
    label: '',
    type: '',
    formDataKey: '',
    defaultValue: undefined,
    showEditor: false,
    showTextArea: false,
  });

  const handleActionClick = (data: UpdateData) => {
    if (isAppliedChanges) {
      setPendingUpdate(data);
      setIsConfirmOpen(true);
    } else {
      setUpdateData(data);
    }
  };

  const handleConfirm = () => {
    if (pendingUpdate) {
      setUpdateData(pendingUpdate);
    }
    setIsConfirmOpen(false);
    setPendingUpdate(null);
  };

  const handleCancel = () => {
    setIsConfirmOpen(false);
    setPendingUpdate(null);
  };

  return (
    <Stack spacing={3} bgcolor={white} p={3} borderRadius={8} width="100%">
      <UserDetailRow
        label="Jobs Page Headline"
        value={templateData?.job_page_heading ?? 'Join Our Team At'}
        action={() =>
          setUpdateData({
            label: 'Jobs Page Headline',
            type: 'string',
            defaultValue: templateData?.job_page_heading ?? 'Join Our Team At',
            formDataKey: 'job_page_heading',
          })
        }
        isHtml={false}
      />

      <UserDetailRow
        label="Jobs Page Subheader"
        value={templateData?.additional_text ?? ''}
        action={() =>
          setUpdateData({
            label: 'Jobs Page Subheader',
            type: 'string',
            defaultValue: templateData?.additional_text ?? '',
            formDataKey: 'additional_text',
            showEditor: false,
            showTextArea: true,
          })
        }
        isHtml={false}
      />

      <UserDetailRow
        label="Hiring Culture"
        value={templateData?.our_hiring_culture ?? '-'}
        action={() =>
          handleActionClick({
            label: 'Hiring Culture',
            type: 'text',
            defaultValue: templateData?.our_hiring_culture ?? '',
            formDataKey: 'our_hiring_culture',
          })
        }
        isHtml
      />

      <UserDetailRow
        label="Website"
        value={templateData?.s_website_url}
        action={() =>
          handleActionClick({
            label: 'Website',
            type: 's_website_url',
            defaultValue: templateData?.s_website_url,
            formDataKey: 's_website_url',
          })
        }
      />

      <TemplatePageCustomization templateData={templateData} />

      <TemplateCompanyNameModal
        templateData={templateData}
        label={updateData.label}
        showEditor={updateData.label === 'Hiring Culture'}
        type={updateData.type}
        formDataKey={updateData.formDataKey}
        defaultValue={updateData.defaultValue}
        isOpen={!!updateData.label}
        showTextArea={updateData.showTextArea}
        handleClose={() =>
          setUpdateData({
            label: '',
            type: '',
            formDataKey: '',
            defaultValue: undefined,
          })
        }
      />

      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Stack>
  );
};

export default TemplateJobPageCustomizationTab;
